import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Save, MessageCircle, Facebook, Instagram, MapPin, Phone, Mail, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/settings').then(r => setSettings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const updateRule = (idx, k, v) => {
    const rules = [...settings.pricing_rules];
    rules[idx] = { ...rules[idx], [k]: v };
    setSettings(s => ({ ...s, pricing_rules: rules }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      toast.success('Configuración guardada');
      load();
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-white/60">Cargando...</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-white">Configuración</h1>
          <p className="text-white/60">Información de contacto, redes sociales y tarifas</p>
        </div>
        <Button onClick={save} disabled={saving} className="btn-orange border-0 gap-2">
          <Save className="w-4 h-4" /> {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>

      {/* Información general */}
      <div className="glass-card rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#ff6b1a]" /> Información de la Empresa
        </h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className="text-white/80 mb-2 block">Nombre de la Empresa</Label>
            <Input value={settings.company_name} onChange={(e) => update('company_name', e.target.value)} className="bg-black/40 border-white/10 text-white h-11" />
          </div>
          <div>
            <Label className="text-white/80 mb-2 block flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Teléfono</Label>
            <Input value={settings.phone} onChange={(e) => update('phone', e.target.value)} className="bg-black/40 border-white/10 text-white h-11" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-white/80 mb-2 block flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Dirección</Label>
            <Input value={settings.address} onChange={(e) => update('address', e.target.value)} className="bg-black/40 border-white/10 text-white h-11" placeholder="Punta Cana, RD" />
          </div>
          <div>
            <Label className="text-white/80 mb-2 block flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email</Label>
            <Input type="email" value={settings.email} onChange={(e) => update('email', e.target.value)} className="bg-black/40 border-white/10 text-white h-11" />
          </div>
          <div>
            <Label className="text-white/80 mb-2 block flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> Número WhatsApp <span className="text-white/40 text-xs">(sin + ni espacios)</span></Label>
            <Input value={settings.whatsapp_number} onChange={(e) => update('whatsapp_number', e.target.value)} className="bg-black/40 border-white/10 text-white h-11" placeholder="18299838580" />
          </div>
        </div>
      </div>

      {/* Redes Sociales */}
      <div className="glass-card rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-5">Redes Sociales</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className="text-white/80 mb-2 block flex items-center gap-1.5"><Facebook className="w-3.5 h-3.5 text-[#1877F2]" /> Facebook URL</Label>
            <Input value={settings.facebook_url} onChange={(e) => update('facebook_url', e.target.value)} className="bg-black/40 border-white/10 text-white h-11" placeholder="https://facebook.com/tu-pagina" />
          </div>
          <div>
            <Label className="text-white/80 mb-2 block flex items-center gap-1.5"><Instagram className="w-3.5 h-3.5 text-[#E4405F]" /> Instagram URL</Label>
            <Input value={settings.instagram_url} onChange={(e) => update('instagram_url', e.target.value)} className="bg-black/40 border-white/10 text-white h-11" placeholder="https://instagram.com/tu-cuenta" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-white/80 mb-2 block">TikTok URL (opcional)</Label>
            <Input value={settings.tiktok_url} onChange={(e) => update('tiktok_url', e.target.value)} className="bg-black/40 border-white/10 text-white h-11" placeholder="https://tiktok.com/@tu-cuenta" />
          </div>
        </div>
      </div>

      {/* Tarifas */}
      <div className="glass-card rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-[#ff6b1a]" /> Tarifas de Transporte
        </h2>
        <p className="text-white/50 text-sm mb-5">Precio = max(Base + km × Por_km + (pax-1) × Extra/pax, Mínimo). Ida y vuelta usa multiplicador <span className="text-white">{settings.round_trip_multiplier}x</span>.</p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-white/60 bg-black/40">
              <tr>
                <th className="text-left p-2.5">Vehículo</th>
                <th className="text-right p-2.5">Base $</th>
                <th className="text-right p-2.5">$/km</th>
                <th className="text-right p-2.5">Extra/pax $</th>
                <th className="text-right p-2.5">Mínimo $</th>
                <th className="text-right p-2.5">Max pax</th>
                <th className="text-right p-2.5">Max km</th>
                <th className="text-center p-2.5">Por pax</th>
                <th className="text-center p-2.5">Activo</th>
              </tr>
            </thead>
            <tbody>
              {settings.pricing_rules?.map((r, idx) => (
                <tr key={idx} className="border-t border-white/5">
                  <td className="p-2"><Input value={r.label} onChange={(e) => updateRule(idx, 'label', e.target.value)} className="bg-black/40 border-white/10 h-9 text-white text-xs" /></td>
                  <td className="p-2"><Input type="number" step="0.01" value={r.base} onChange={(e) => updateRule(idx, 'base', parseFloat(e.target.value) || 0)} className="bg-black/40 border-white/10 h-9 text-white text-xs text-right w-20" /></td>
                  <td className="p-2"><Input type="number" step="0.01" value={r.per_km} onChange={(e) => updateRule(idx, 'per_km', parseFloat(e.target.value) || 0)} className="bg-black/40 border-white/10 h-9 text-white text-xs text-right w-20" /></td>
                  <td className="p-2"><Input type="number" step="0.01" value={r.extra_per_pax || 0} onChange={(e) => updateRule(idx, 'extra_per_pax', parseFloat(e.target.value) || 0)} className="bg-black/40 border-white/10 h-9 text-white text-xs text-right w-20" /></td>
                  <td className="p-2"><Input type="number" step="0.01" value={r.minimum} onChange={(e) => updateRule(idx, 'minimum', parseFloat(e.target.value) || 0)} className="bg-black/40 border-white/10 h-9 text-white text-xs text-right w-20" /></td>
                  <td className="p-2"><Input type="number" value={r.max_pax} onChange={(e) => updateRule(idx, 'max_pax', parseInt(e.target.value) || 1)} className="bg-black/40 border-white/10 h-9 text-white text-xs text-right w-16" /></td>
                  <td className="p-2"><Input type="number" value={r.max_distance_km || ''} onChange={(e) => updateRule(idx, 'max_distance_km', e.target.value ? parseFloat(e.target.value) : null)} className="bg-black/40 border-white/10 h-9 text-white text-xs text-right w-16" placeholder="∞" /></td>
                  <td className="p-2 text-center"><Switch checked={r.per_passenger} onCheckedChange={(v) => updateRule(idx, 'per_passenger', v)} /></td>
                  <td className="p-2 text-center"><Switch checked={r.enabled} onCheckedChange={(v) => updateRule(idx, 'enabled', v)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 max-w-xs">
          <Label className="text-white/80 mb-2 block">Multiplicador Ida y Vuelta</Label>
          <Input type="number" step="0.05" value={settings.round_trip_multiplier} onChange={(e) => update('round_trip_multiplier', parseFloat(e.target.value) || 1.85)} className="bg-black/40 border-white/10 text-white h-11" />
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
